import { styled } from '@mui/material';

import { Stack } from '../../atoms/Stack';
import { Tag } from '../../atoms/Tag';
import { Typography } from '../../atoms/Typography';
import { Card } from '../Card';
import { CardContent } from '../CardContent';
import { ResourceMenuActions } from '../../organisms/ResourceMenuActions';
import { CommunicationDocuments, CommunicationPeople } from './CommunicationDetails';
import { CommunicationPreview } from './CommunicationPreview';
import {
  type CommunicationCardAction,
  type CommunicationCardProps,
  type CommunicationDocument,
  type CommunicationItem,
  type CommunicationType,
  useCommunicationCard,
} from './useCommunicationCard';

type Props = CommunicationCardProps;

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

const HeaderMeta = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
  flex: 1,
}));

const HeaderActions = styled('div')({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
});

const TitleText = styled(Typography)({
  wordBreak: 'break-word',
});

export function CommunicationCard(props: Props) {
  const hook = useCommunicationCard(props);
  const Icon = hook.IconComponent;

  return (
    <Card data-testid={props['data-testid']}>
      <CardContent>
        <Stack spacing={1}>
          <HeaderContainer>
            <HeaderMeta>
              {hook.isExchange ? (
                <Tag
                  color={hook.tagColor}
                  label={hook.typeLabel}
                  icon={<Icon />}
                />
              ) : null}

              {hook.formattedDate ? (
                <Typography variant="caption">
                  {hook.formattedDate}
                </Typography>
              ) : null}
            </HeaderMeta>

            <HeaderActions>
              {props.action ? (
                props.action
              ) : hook.hasActions ? (
                <ResourceMenuActions
                  size="small"
                  actions={hook.actions}
                  tooltip={hook.actionsButtonTooltip}
                  ariaLabel={hook.actionsButtonAriaLabel}
                />
              ) : null}
            </HeaderActions>
          </HeaderContainer>

          {props.item.title ? (
            <TitleText variant="body2Emphasis">
              {props.item.title}
            </TitleText>
          ) : null}

          <CommunicationPeople
            providerName={hook.providerName}
            contactName={hook.contactName}
            isExchange={hook.isExchange}
          />

          <CommunicationPreview
            item={props.item}
            documents={hook.documents}
            maxLines={props.maxCommentLines}
          />

          <CommunicationDocuments documents={hook.documents} />
        </Stack>
      </CardContent>
    </Card>
  );
}

export type {
  CommunicationCardAction,
  CommunicationCardProps,
  CommunicationDocument,
  CommunicationItem,
  CommunicationType,
};
